// frontend/src/services/apiService.js

import axios from 'axios';
import { jwtDecode } from 'jwt-decode';

const API_URL = process.env.VUE_APP_API_URL;

// Create axios instance with base configuration
const apiClient = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Function to get auth token from localStorage
const getAuthToken = () => {
  return localStorage.getItem('authToken');
};

// Function to get current user data from localStorage
const getCurrentUser = () => {
  const userStr = localStorage.getItem('currentUser');
  return userStr ? JSON.parse(userStr) : null;
};

// Function to clear auth data
const clearAuthData = () => {
  localStorage.removeItem('authToken');
  localStorage.removeItem('currentUser');
  localStorage.clear(); // this removes all keys, including legacy ones
};



// Request interceptor to add auth token
apiClient.interceptors.request.use(
  (config) => {
    const token = getAuthToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle 401 errors
apiClient.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    if (error.response && error.response.status === 401) {
      // Clear auth data and redirect to login
      clearAuthData();
      // Check if we're not already on login page to avoid infinite redirect
      if (window.location.pathname !== '/login') {
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

/**
 * Pull the filename out of a Content-Disposition header. The server names
 * exports with the survey slug and the date, which is worth keeping: a
 * folder of files called "export.xlsx" helps nobody.
 */
function parseFilename(disposition) {
  if (!disposition) return null;
  const match = /filename="?([^";]+)"?/i.exec(disposition);
  return match ? match[1] : null;
}

export default {
  // Auth methods
  getAuthToken,
  getCurrentUser,
  clearAuthData,

// Login method
async login(username, password) {
  try {
    const formData = new URLSearchParams();
    formData.append('username', username);
    formData.append('password', password);

    const response = await axios.post(`${API_URL}/token`, formData.toString(), {
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded'
      }
    });

    // Handle different response types from backend
    if (response.data.requiresPasswordReset) {
      return {
        success: false,
        requiresPasswordReset: true,
        error: 'You must reset your password before logging in'
      };
    }

    if (response.data.accountInactive) {
      return {
        success: false,
        accountInactive: true,
        error: 'Your account is inactive. Please contact an administrator.'
      };
    }

    if (response.data.access_token) {
      // Store auth data using correct keys for the new system
      localStorage.setItem('authToken', response.data.access_token);

      // Store user data as JSON object
      const userData = {
        id: response.data.userId,
        username: username,
        role: response.data.role,
        org_id: response.data.org_id
      };
      localStorage.setItem('currentUser', JSON.stringify(userData));

      // Keep legacy keys for backward compatibility (if needed)
      localStorage.setItem('token', response.data.access_token);
      localStorage.setItem('role', response.data.role);
      localStorage.setItem('username', username);
      localStorage.setItem('userId', response.data.userId);
      localStorage.setItem('org_id', response.data.org_id);
      localStorage.setItem('loggedIn', 'true'); // For legacy compatibility

      return {
        success: true,
        user: userData,
        token: response.data.access_token
      };
    } else {
      return {
        success: false,
        error: 'Login failed. Please try again.'
      };
    }
  } catch (error) {
    console.error('Login error:', error);
    let errorMessage = 'An error occurred while trying to log in';

    if (error.response?.status === 401) {
      errorMessage = 'Invalid username or password';
    }

    return {
      success: false,
      error: errorMessage
    };
  }
},

  async fetchUsers() {
    // console.log('Fetching users from API...');
    const response = await apiClient.get('/users');
    return response.data.users;
  },

  async fetchClasses(userId) {
    try {
      const response = await apiClient.get('/classes', {
        params: { user_id: userId }
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching classes:', error);
      throw error;
    }
  },

  async fetchAssessments(userId) {
    const response = await apiClient.get('/assessments/all', {
      params: { user_id: userId },
    });
    return response.data;
  },

  async fetchStudentsInClass(classId) {
    try {
      const response = await apiClient.get(`/classes/${classId}/students`);
      return response.data;
    } catch (error) {
      console.error('Error fetching students in class:', error);
      throw error;
    }
  },

  async fetchStudents(assessmentId) {
    // console.log(`Fetching students for assessment ID ${assessmentId} from API...`);
    const response = await apiClient.get(`/assessments/${assessmentId}/students`);
    return response.data;
  },

  async fetchAvailableLanguages() {
    try {
      // console.log('Fetching available languages...');
      const response = await apiService.fetchSurveyTranslations();
      this.surveyData = response;
      this.availableLanguages = Object.keys(this.surveyData);
      // console.log('Available languages:', this.availableLanguages);
    } catch (error) {
      console.error('Error fetching available languages:', error);
    }
  },

  async fetchSurveyTranslations() {
    // console.log('Starting fetchSurveyTranslations...');
    try {
      // This endpoint is public - no auth needed
      const response = await axios.get(`${API_URL}/api/questions/translations`);
      // console.log('Raw API response:', response);

      if (response.data && response.data.questions) {
        // console.log('Survey translations fetched successfully');
        // console.log('Translations structure:', Object.keys(response.data.questions));
        return response.data.questions;
      } else {
        // console.warn('No survey translations found in the response');
        // console.log('Full response data:', response.data);
        return {};
      }
    } catch (error) {
      console.error('Error fetching survey translations:', error);
      if (error.response) {
        console.error('Error response:', error.response.data);
        console.error('Error status:', error.response.status);
      }
      throw error;
    }
  },

  async fetchAmbianceResponses(userId, assessmentId, lang = 'en') {
    // console.log(`apiService.js:fetchAmbianceResponses: Fetching ambiance responses for user ID ${userId}, assessment ID ${assessmentId}, language ${lang}`);

    try {
      const response = await apiClient.get('/api/questions/ambiance_responses', {
        params: { user_id: userId, assessment_id: assessmentId, lang },
      });
      // console.log('apiService.js:fetchAmbianceResponses: Ambiance responses fetched successfully:', response.data);

      return response.data;
    } catch (error) {
      console.error('apiService.js:fetchAmbianceResponses: Error fetching ambiance responses:', error);
      if (error.response) {
        console.error('Server responded with a status:', error.response.status);
        console.error('Server response data:', error.response.data);
      } else if (error.request) {
        console.error('No response received from the server:', error.request);
      } else {
        console.error('Error setting up the request:', error.message);
      }
      throw error;
    }
  },

  async fetchNetworkResponses(userId, assessmentId, lang = 'en') {
    // console.log('START: VVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVV');
    // console.log(`apiService.js:fetchNetworkResponses: Fetching network responses for user ID ${userId}, assessment ID ${assessmentId}, language ${lang}`);

    try {
      // console.log(`Sending GET request to: ${API_URL}/api/questions/network_responses`);
      // console.log('Request parameters:', { user_id: userId, assessment_id: assessmentId, lang });

      const response = await apiClient.get('/api/questions/network_responses', {
        params: { user_id: userId, assessment_id: assessmentId, lang },
      });

      // console.log('Response status:', response.status);
      // console.log('Response headers:', response.headers);
      // console.log('apiService.js:fetchNetworkResponses: Network responses fetched successfully:', response.data);
      return response.data;
    } catch (error) {
      console.error('Error in fetchNetworkResponses:', error);
      if (error.response) {
        console.error('Error data:', error.response.data);
        console.error('Error status:', error.response.status);
        console.error('Error headers:', error.response.headers);
      } else if (error.request) {
        console.error('No response received:', error.request);
      } else {
        console.error('Error message:', error.message);
      }
      throw error;
    } finally {
      // console.log('END: VVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVV');
    }
  },



  exportAssessment: async (exportRequest) => {
    try {
      // console.log('apiService.exportAssessment: Exporting assessment:', exportRequest);
      // console.log(API_URL); // This should output 'http://localhost:8000' in development
      const response = await apiClient.post('/export-assessment', exportRequest, {
        responseType: 'blob'
      });
      return response;
    } catch (error) {
      console.error('Error in exportAssessment:', error);
      throw error;
    }
  },

  async fetchTextResponses(assessmentId) {
    // console.log(`Fetching text responses for assessment ID ${assessmentId}`);
    try {
      const response = await apiClient.get('/api/text_responses', {
        params: { assessment_id: assessmentId }
      });
      // console.log('Text responses fetched successfully:', response.data);
      return response.data.responses;
    } catch (error) {
      console.error('Error fetching text responses:', error);
      if (error.response) {
        console.error('Error data:', error.response.data);
        console.error('Error status:', error.response.status);
        console.error('Error headers:', error.response.headers);
      } else if (error.request) {
        console.error('No response received:', error.request);
      } else {
        console.error('Error message:', error.message);
      }
      throw error;
    }
  },

  async fetchTextQuestionLanguages() {
    try {
      const response = await apiClient.get('/api/textquestions/languages');
      return response.data;
    } catch (error) {
      console.error('Error fetching text question languages:', error);
      throw error;
    }
  },

  async fetchTextQuestionTranslations(lang) {
    try {
      const response = await apiClient.get(`/api/textquestions/translations/${lang}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching text question translations:', error);
      throw error;
    }
  },

  async exportNetworkSurvey(exportRequest) {
    try {
      const response = await apiClient.post('/export-network-survey', exportRequest, {
        responseType: 'blob',
      });
      return response;
    } catch (error) {
      console.error('Error in exportNetworkSurvey:', error);
      throw error;
    }
  },

  async exportAggregatedData(exportRequest) {
    try {
      const response = await apiClient.post('/export-aggregated-data', exportRequest, {
        responseType: 'blob',
      });
      return response;
    } catch (error) {
      console.error('Error in exportAggregatedData:', error);
      throw error;
    }
  },

  async fetchNetworkMetrics(userId, assessmentId, lang = 'en') {
    try {
      const response = await apiClient.get('/api/questions/network_metrics', {
        params: { user_id: userId, assessment_id: assessmentId, lang },
      });
      return response.data;
    } catch (error) {
      console.error('Error in fetchNetworkMetrics:', error);
      throw error;
    }
  },

  async setDefaultNetworkSurvey(surveyId) {
    try {
      const response = await apiClient.put(`/api/networksurveys/${surveyId}/set-default`, {
        headers: { 'Content-Type': 'application/json' },
      });
      return response.data;
    } catch (error) {
      console.error('Error setting default network survey:', error);
      throw error;
    }
  },

  async fetchDefaultNetworkSurvey() {
    try {
      const response = await apiClient.get('/api/networksurveys/default');
      return response.data;
    } catch (error) {
      console.error('Error fetching default network survey:', error);
      throw error;
    }
  },

  async calculateMDSPositions(nodes, links, width = 1280, height = 640, padding = 64) {
    console.log('apiService.js:calculateMDSPositions: Calculating MDS positions on backend (Nonmetric MDS)');
    try {
      const response = await apiClient.post('/api/calculate', {
        nodes: nodes,
        links: links,
        width: width,
        height: height,
        padding: padding
      });

      console.log('apiService.js:calculateMDSPositions: Raw response:', response);
      console.log('apiService.js:calculateMDSPositions: Response data:', response.data);
      console.log('apiService.js:calculateMDSPositions: Response status:', response.status);

      // Check if response is valid
      if (response.status === 200 && response.data) {
        if (response.data.positions) {
          console.log('apiService.js:calculateMDSPositions: Backend Nonmetric MDS calculation successful:', {
            success: response.data.success,
            positionsCount: response.data.positions.length,
            error: response.data.error
          });
          return response.data;
        } else {
          console.warn('apiService.js:calculateMDSPositions: Response missing positions field:', response.data);
          throw new Error(`Backend response missing positions field: ${JSON.stringify(response.data)}`);
        }
      } else {
        console.warn('apiService.js:calculateMDSPositions: Invalid response status or data:', {
          status: response.status,
          data: response.data
        });
        throw new Error(`Invalid response: status ${response.status}`);
      }

    } catch (error) {
      console.error('apiService.js:calculateMDSPositions: Error calculating MDS positions:', error);
      if (error.response) {
        console.error('apiService.js:calculateMDSPositions: Error response:', error.response.data);
        console.error('apiService.js:calculateMDSPositions: Error status:', error.response.status);
      }
      throw error;
    }
  },

  // Student survey submission methods (these are public endpoints - no auth required)
  async submitAmbianceResponses(surveySubmission) {
    try {
      // Use direct axios without auth for student submissions
      const response = await axios.post(`${API_URL}/api/ambiancequestions/responses`, surveySubmission);
      return response.data;
    } catch (error) {
      console.error('Error submitting ambiance responses:', error);
      throw error;
    }
  },

  async submitNetworkResponses(networkResponses) {
    try {
      // Use direct axios without auth for student submissions
      const response = await axios.post(`${API_URL}/networkresponses`, networkResponses);
      return response.data;
    } catch (error) {
      console.error('Error submitting network responses:', error);
      throw error;
    }
  },

  async submitTextResponses(textResponses) {
    try {
      // Use direct axios without auth for student submissions
      const response = await axios.post(`${API_URL}/textresponses`, textResponses);
      return response.data;
    } catch (error) {
      console.error('Error submitting text responses:', error);
      throw error;
    }
  },

  // Public endpoint methods (no auth required)
  async verifyAssessment(pin) {
    try {
      const response = await axios.post(`${API_URL}/verify-assessment`, { pin });
      return response.data;
    } catch (error) {
      console.error('Error verifying assessment:', error);
      throw error;
    }
  },

  async getAssessmentStatus(pin) {
    try {
      const response = await axios.post(`${API_URL}/get-assessment-status`, { pin });
      return response.data;
    } catch (error) {
      console.error('Error getting assessment status:', error);
      throw error;
    }
  },

  async getAssessmentsFromPin(pin) {
    try {
      const response = await axios.post(`${API_URL}/verify-assessment`, { pin: parseInt(pin) });
      return response.data;
    } catch (error) {
      console.error('Error getting assessments from PIN:', error);
      throw error;
    }
  },

  // Public endpoints for survey questions (no auth required for students)
  async fetchNetworkQuestions(lang = 'en') {
    try {
      const response = await axios.get(`${API_URL}/api/networkquestions/translations`, {
        params: { lang }
      });
      console.log('apiService.fetchNetworkQuestions response:', response.data);
      return response.data;
    } catch (error) {
      console.error('Error fetching network questions:', error);
      throw error;
    }
  },

  async fetchTextQuestions(lang = 'en') {
    try {
      const response = await axios.get(`${API_URL}/api/textquestions/translations`, {
        params: { lang }
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching text questions:', error);
      throw error;
    }
  },

  // Assessment management methods (protected endpoints)
  async getAssessmentById(assessmentId) {
    try {
      const response = await apiClient.get(`/assessments/${assessmentId}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching assessment by ID:', error);
      throw error;
    }
  },

  async createAssessment(assessmentData) {
    try {
      const response = await apiClient.post('/assessments', assessmentData);
      return response.data;
    } catch (error) {
      console.error('Error creating assessment:', error);
      throw error;
    }
  },

  async updateAssessment(assessmentId, assessmentData) {
    try {
      const response = await apiClient.put(`/assessments/${assessmentId}`, assessmentData);
      return response.data;
    } catch (error) {
      console.error('Error updating assessment:', error);
      throw error;
    }
  },

  async deleteAssessment(assessmentId) {
    try {
      const response = await apiClient.delete(`/assessments/${assessmentId}`);
      return response.data;
    } catch (error) {
      console.error('Error deleting assessment:', error);
      throw error;
    }
  },

  // Survey management methods (protected endpoints)
  async fetchNetworkSurveys() {
    try {
      const response = await apiClient.get('/api/networksurveys');
      return response.data;
    } catch (error) {
      console.error('Error fetching network surveys:', error);
      throw error;
    }
  },

  async getNetworkSurveyById(surveyId) {
    try {
      const response = await apiClient.get(`/networksurveys/${surveyId}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching network survey by ID:', error);
      throw error;
    }
  },

  async createNetworkSurvey(surveyData) {
    try {
      const response = await apiClient.post('/networksurveys', surveyData);
      return response.data;
    } catch (error) {
      console.error('Error creating network survey:', error);
      throw error;
    }
  },

  async deleteNetworkSurvey(surveyId) {
    try {
      const response = await apiClient.delete(`/api/networksurveys/${surveyId}`);
      return response.data;
    } catch (error) {
      console.error('Error deleting network survey:', error);
      throw error;
    }
  },

  async setDefaultNetworkSurvey(surveyId) {
    try {
      const response = await apiClient.put(`/api/networksurveys/${surveyId}/set-default`);
      return response.data;
    } catch (error) {
      console.error('Error setting default network survey:', error);
      throw error;
    }
  },

  async deleteAmbianceSurvey(surveyId) {
    try {
      const response = await apiClient.delete(`/api/ambiance_surveys/${surveyId}`);
      return response.data;
    } catch (error) {
      console.error('Error deleting ambiance survey:', error);
      throw error;
    }
  },

  // Language support methods (public endpoints)
  async fetchAvailableQuestionLanguages() {
    try {
      const response = await axios.get(`${API_URL}/api/questions/languages`);
      return response.data;
    } catch (error) {
      console.error('Error fetching available question languages:', error);
      throw error;
    }
  },

  async fetchQuestionTranslations(lang = 'en') {
    try {
      const response = await axios.get(`${API_URL}/api/questions/translations/${lang}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching question translations:', error);
      throw error;
    }
  },

  // Questions methods (public endpoints)
  async fetchQuestions() {
    try {
      const response = await axios.get(`${API_URL}/api/questions`);
      return response.data;
    } catch (error) {
      console.error('Error fetching questions:', error);
      throw error;
    }
  },

  async fetchAllQuestionTranslations() {
    try {
      const response = await axios.get(`${API_URL}/api/questions/translations`);
      return response.data;
    } catch (error) {
      console.error('Error fetching all question translations:', error);
      throw error;
    }
  },

  // Network questions methods (public endpoints)  
  async fetchAllNetworkQuestions() {
    try {
      const response = await axios.get(`${API_URL}/api/networkquestions`);
      return response.data;
    } catch (error) {
      console.error('Error fetching network questions:', error);
      throw error;
    }
  },

  async fetchNetworkQuestionTranslations() {
    try {
      const response = await axios.get(`${API_URL}/api/networkquestions/translations`);
      return response.data;
    } catch (error) {
      console.error('Error fetching network question translations:', error);
      throw error;
    }
  },

  // Text questions methods (public endpoints)
  async fetchAllTextQuestionTranslations() {
    try {
      const response = await axios.get(`${API_URL}/api/textquestions/translations`);
      return response.data;
    } catch (error) {
      console.error('Error fetching text question translations:', error);
      throw error;
    }
  },

  // Student management methods (protected endpoints)
  async fetchStudents() {
    try {
      const response = await apiClient.get('/students');
      return response.data;
    } catch (error) {
      console.error('Error fetching students:', error);
      throw error;
    }
  },

  async addStudent(studentData) {
    try {
      const response = await apiClient.post('/add-student', {
        name: studentData.name,
        email: studentData.email,
        gender: studentData.gender
      });
      return response.data;
    } catch (error) {
      console.error('Error adding student:', error);
      throw error;
    }
  },

  async deleteStudent(studentId) {
    try {
      const response = await apiClient.delete(`/delete-student/${studentId}`);
      return response.data;
    } catch (error) {
      console.error('Error deleting student:', error);
      throw error;
    }
  },

  // Feedback methods (protected endpoints)
  async submitFeedback(feedbackData) {
    try {
      const response = await apiClient.post('/feedback', feedbackData);
      return response.data;
    } catch (error) {
      console.error('Error submitting feedback:', error);
      throw error;
    }
  },

  async fetchFeedback() {
    try {
      const response = await apiClient.get('/feedback');
      return response.data;
    } catch (error) {
      console.error('Error fetching feedback:', error);
      throw error;
    }
  },

  // Chat/Messages methods (protected endpoints)
  async fetchChatMessages(chatId) {
    try {
      const response = await apiClient.get('/messages_chat', {
        params: { chat_id: chatId }
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching chat messages:', error);
      throw error;
    }
  },

  async sendChatMessage(chatId, messageData) {
    try {
      const response = await apiClient.post(`/messages_chat?chat_id=${chatId}`, messageData);
      return response.data;
    } catch (error) {
      console.error('Error sending chat message:', error);
      throw error;
    }
  },

  async fetchMessages(userId) {
    try {
      const response = await apiClient.get('/messages', {
        params: { user_id: userId }
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching messages:', error);
      throw error;
    }
  },

  async sendMessage(messageData) {
    try {
      const response = await apiClient.post('/messages', messageData);
      return response.data;
    } catch (error) {
      console.error('Error sending message:', error);
      throw error;
    }
  },

  async replyToMessage(messageId, replyData) {
    try {
      const response = await apiClient.post(`/messages/${messageId}/reply`, replyData);
      return response.data;
    } catch (error) {
      console.error('Error replying to message:', error);
      throw error;
    }
  },

  // Class management methods (protected endpoints)
  async createClass(classData) {
    try {
      const response = await apiClient.post('/classes', classData);
      return response.data;
    } catch (error) {
      console.error('Error creating class:', error);
      throw error;
    }
  },



  async addStudentToClass(classId, studentId) {
    try {
      const response = await apiClient.post(`/classes/${classId}/students`, {
        class_id: classId,
        student_id: studentId
      });
      return response.data;
    } catch (error) {
      console.error('Error adding student to class:', error);
      throw error;
    }
  },

  async removeStudentFromClass(classId, studentId) {
    try {
      const response = await apiClient.delete(`/classes/${classId}/students/${studentId}`);
      return response.data;
    } catch (error) {
      console.error('Error removing student from class:', error);
      throw error;
    }
  },



  // User management methods (protected endpoints)
  async createUser(userData) {
    try {
      const response = await apiClient.post('/users', userData);
      return response.data;
    } catch (error) {
      console.error('Error creating user:', error);
      throw error;
    }
  },

  async updateUser(userId, userData) {
    try {
      const response = await apiClient.put(`/users/${userId}`, userData);
      return response.data;
    } catch (error) {
      console.error('Error updating user:', error);
      throw error;
    }
  },

  async deleteUser(userId) {
    try {
      const response = await apiClient.delete(`/users/${userId}`);
      return response.data;
    } catch (error) {
      console.error('Error deleting user:', error);
      throw error;
    }
  },

  async fetchRoles() {
    try {
      const response = await apiClient.get('/roles');
      return response.data;
    } catch (error) {
      console.error('Error fetching roles:', error);
      throw error;
    }
  },

  async fetchUserLogins() {
    try {
      const response = await apiClient.get('/logins');
      return response.data;
    } catch (error) {
      console.error('Error fetching user logins:', error);
      throw error;
    }
  },

  // SEQ Admin — questionnaires
  async fetchQuestionnaires() {
    // GET /api/seq/questionnaires
    // Returns list of {id, name, slug, description, status, is_locked,
    //                  created_at, updated_at, created_by}
    // Auth required. Backend scopes to caller's organization and enforces
    // admin / org_admin role.
    try {
      const response = await apiClient.get('/api/seq/questionnaires');
      return response.data;
    } catch (error) {
      console.error('Error fetching SEQ questionnaires:', error);
      throw error;
    }
  },

  async fetchQuestionnaireBySlug(slug) {
    // GET /api/seq/questionnaires/:slug
    // Returns the full row {id, name, slug, description, status, is_locked,
    //                       created_at, updated_at, created_by}
    // 404 if not found / belongs to another org. Auth required.
    try {
      const response = await apiClient.get(
        `/api/seq/questionnaires/${encodeURIComponent(slug)}`
      );
      return response.data;
    } catch (error) {
      console.error('Error fetching SEQ questionnaire by slug:', error);
      throw error;
    }
  },

  async createQuestionnaire(payload) {
    // POST /api/seq/questionnaires
    // Body: { name, slug, description?, status? }
    // Returns the created row: {id, name, slug, description, status,
    //                            is_locked, created_at, updated_at, created_by}
    // Auth required. Backend assigns organization_id from the bearer token.
    // 409 if (organization_id, slug) already exists; 422 on validation
    // failure (slug regex, missing name, etc.). Error response is rethrown
    // so the caller can read error.response.data.detail.
    try {
      const response = await apiClient.post('/api/seq/questionnaires', payload);
      return response.data;
    } catch (error) {
      console.error('Error creating SEQ questionnaire:', error);
      throw error;
    }
  },

  async updateQuestionnaire(id, payload) {
    // PATCH /api/seq/questionnaires/:id
    // Body (all fields optional): { name?, description?, status? }
    // Returns the full updated row.
    // 404 if not found / wrong org. 409 if is_locked is TRUE.
    // 422 on validation failure (name min/max length, status enum).
    // Slug and is_locked are intentionally not accepted; the backend
    // will silently drop them if sent.
    try {
      const response = await apiClient.patch(`/api/seq/questionnaires/${id}`, payload);
      return response.data;
    } catch (error) {
      console.error('Error updating SEQ questionnaire:', error);
      throw error;
    }
  },

  async fetchSections() {
    // GET /api/seq/sections
    // Returns sections across all questionnaires in the current user's org:
    //   [{id, slug, name (i18n dict), position, questionnaire_id,
    //     questionnaire_slug, questionnaire_name, created_at, updated_at}]
    // Used by the question bank's section filter and the editor's
    // section dropdown.
    try {
      const response = await apiClient.get('/api/seq/sections');
      return response.data;
    } catch (error) {
      console.error('Error fetching SEQ sections:', error);
      throw error;
    }
  },

  // SEQ Admin — questions (org-scoped question library)
  async fetchQuestions({ question_type = null, status = 'active' } = {}) {
    // GET /api/seq/questions?question_type=&status=
    // Returns list of {id, organization_id, section_id, code, question_type,
    //                  content, help_text, status, created_at, updated_at,
    //                  created_by, answer_count}
    // status accepts 'active' (default), 'archived', or 'all'.
    // Auth required. Backend scopes to caller's organization.
    try {
      const params = {};
      if (question_type) params.question_type = question_type;
      if (status) params.status = status;
      const response = await apiClient.get('/api/seq/questions', { params });
      return response.data;
    } catch (error) {
      console.error('Error fetching SEQ questions:', error);
      throw error;
    }
  },

  async fetchQuestion(id) {
    // GET /api/seq/questions/:id
    // Returns the full row with content + help_text as parsed dicts.
    // 404 if not found / belongs to another org. Auth required.
    try {
      const response = await apiClient.get(`/api/seq/questions/${id}`);
      return response.data;
    } catch (error) {
      console.error(`Error fetching SEQ question ${id}:`, error);
      throw error;
    }
  },

  async createQuestion(payload) {
    // POST /api/seq/questions
    // Body: { code, question_type, content, help_text?, section_id?, status? }
    // Returns the created row including server-assigned id and answer_count=0.
    // 409 if (org, code) already exists; 422 on validation failure.
    try {
      const response = await apiClient.post('/api/seq/questions', payload);
      return response.data;
    } catch (error) {
      console.error('Error creating SEQ question:', error);
      throw error;
    }
  },

  async updateQuestion(id, payload) {
    // PUT /api/seq/questions/:id
    // Body (all fields optional): { code?, content?, help_text?,
    //                                section_id?, status? }
    // Returns the full updated row. 404 if not found / wrong org.
    // 409 if code change collides. question_type is immutable.
    try {
      const response = await apiClient.put(`/api/seq/questions/${id}`, payload);
      return response.data;
    } catch (error) {
      console.error(`Error updating SEQ question ${id}:`, error);
      throw error;
    }
  },

  async archiveQuestion(id) {
    // POST /api/seq/questions/:id/archive
    // Soft-delete: sets status to 'archived'. Returns the updated row.
    try {
      const response = await apiClient.post(`/api/seq/questions/${id}/archive`);
      return response.data;
    } catch (error) {
      console.error(`Error archiving SEQ question ${id}:`, error);
      throw error;
    }
  },

  async restoreQuestion(id) {
    // POST /api/seq/questions/:id/restore
    // Reverse archive: sets status back to 'active'. Returns the updated row.
    try {
      const response = await apiClient.post(`/api/seq/questions/${id}/restore`);
      return response.data;
    } catch (error) {
      console.error(`Error restoring SEQ question ${id}:`, error);
      throw error;
    }
  },

  // SEQ Admin — surveys
  async fetchSurveys() {
    // GET /api/seq/surveys
    // Returns list of {id, questionnaire_id, name, slug, description,
    //                  starts_at, ends_at, status, created_at,
    //                  updated_at, created_by}
    // Auth required. Backend JOINs to seq_questionnaires and filters by
    // the caller's org.
    try {
      const response = await apiClient.get('/api/seq/surveys');
      return response.data;
    } catch (error) {
      console.error('Error fetching SEQ surveys:', error);
      throw error;
    }
  },

  async fetchSurveyBySlug(slug) {
    // GET /api/seq/surveys/:slug
    // Returns the full row. 404 if not found / belongs to another org.
    try {
      const response = await apiClient.get(
        `/api/seq/surveys/${encodeURIComponent(slug)}`
      );
      return response.data;
    } catch (error) {
      console.error('Error fetching SEQ survey by slug:', error);
      throw error;
    }
  },

  async createSurvey(payload) {
    // POST /api/seq/surveys
    // Body: { questionnaire_id, name, slug, description?,
    //         starts_at?, ends_at?, status? }
    // Returns the created row including id, created_at, updated_at.
    // 400 if the linked questionnaire is not in your org.
    // 409 if slug already taken (globally unique on this table).
    // 422 on validation failure (slug regex, time window).
    try {
      const response = await apiClient.post('/api/seq/surveys', payload);
      return response.data;
    } catch (error) {
      console.error('Error creating SEQ survey:', error);
      throw error;
    }
  },

  async updateSurvey(id, payload) {
    // PATCH /api/seq/surveys/:id
    // Body (all optional): { name?, description?, starts_at?, ends_at?, status? }
    // questionnaire_id and slug are intentionally not accepted; the
    // backend silently drops them if sent. Pass null for starts_at/
    // ends_at/description to clear those nullable columns.
    // 404 if not found / wrong org. 422 if the merged time window is
    // invalid.
    try {
      const response = await apiClient.patch(`/api/seq/surveys/${id}`, payload);
      return response.data;
    } catch (error) {
      console.error('Error updating SEQ survey:', error);
      throw error;
    }
  },

  async deleteSurvey(id) {
    // DELETE /api/seq/surveys/:id
    // 204 No Content on success.
    // 404 if not found / belongs to another org.
    // 409 if status is 'archived' (must un-archive first).
    try {
      await apiClient.delete(`/api/seq/surveys/${id}`);
    } catch (error) {
      console.error('Error deleting SEQ survey:', error);
      throw error;
    }
  },

  async deleteQuestionnaire(id) {
    // DELETE /api/seq/questionnaires/:id
    // 204 No Content on success.
    // 404 if not found (or belongs to another org).
    // 409 if is_locked is TRUE, or if status is 'archived'.
    // Error response is rethrown so the caller can read
    // error.response.data.detail and error.response.status.
    try {
      await apiClient.delete(`/api/seq/questionnaires/${id}`);
    } catch (error) {
      console.error('Error deleting SEQ questionnaire:', error);
      throw error;
    }
  },

  // SEQ Admin — sections and cards (read-only in this iteration)
  async fetchSectionsForQuestionnaire(slug) {
    // GET /api/seq/questionnaires/:slug/sections
    // Returns list of {id, slug, position, name, created_at, updated_at}
    // ordered by position. `name` is a multilingual JSONB dict.
    // 404 if the questionnaire is not in the caller's org.
    try {
      const response = await apiClient.get(
        `/api/seq/questionnaires/${encodeURIComponent(slug)}/sections`
      );
      return response.data;
    } catch (error) {
      console.error('Error fetching SEQ sections:', error);
      throw error;
    }
  },

  async fetchCardsForQuestionnaire(slug) {
    // GET /api/seq/questionnaires/:slug/cards
    // DORMANT after junction Step 2: the questionnaire detail page now
    // reads from fetchQuestionnaireItems() instead. This method is kept
    // so the legacy code path still imports cleanly and so a later
    // cleanup pass can remove both ends together.
    try {
      const response = await apiClient.get(
        `/api/seq/questionnaires/${encodeURIComponent(slug)}/cards`
      );
      return response.data;
    } catch (error) {
      console.error('Error fetching SEQ cards:', error);
      throw error;
    }
  },

  async fetchQuestionnaireItems(slug) {
    // GET /api/seq/questionnaires/:slug/items
    // Returns the resolved item list from seq_questionnaire_items with
    // its source row inlined under `item`. Each entry carries an
    // `item_type` of 'question' | 'content_card' | 'flow_card' computed
    // by the backend from which FK is non-null on the junction row.
    // Sorted by section then by item position; items with section_id
    // null appear first. 404 if the questionnaire is not in the
    // caller's org; 200 with [] when the questionnaire has no items.
    try {
      const response = await apiClient.get(
        `/api/seq/questionnaires/${encodeURIComponent(slug)}/items`
      );
      return response.data;
    } catch (error) {
      console.error('Error fetching questionnaire items:', error);
      throw error;
    }
  },

  // ─── Participant write path (unauthenticated) ─────────────────────
  // Participants are anonymous, so these carry no bearer token. The
  // resume_token in the path is the only credential and grants access to
  // exactly one submission. See docs/CONTRACT_DATA_CAPTURE.md.

  async fetchSurveyRuntime(surveySlug) {
    // GET /api/seq/surveys/:slug/runtime
    // { open, survey, revision, payload } where payload is the frozen
    // questionnaire the participant answers. 409 when the survey exists
    // but is not open, with a `reason` naming which kind of closed.
    try {
      const response = await apiClient.get(
        `/api/seq/surveys/${encodeURIComponent(surveySlug)}/runtime`
      );
      return response.data;
    } catch (error) {
      console.error('Error fetching survey runtime:', error);
      throw error;
    }
  },

  async createSubmission(surveySlug, payload) {
    // POST /api/seq/surveys/:slug/submissions
    // Called at the moment consent is given and never before: this call is
    // what brings the row into existence, so a participant who declines
    // leaves no trace. Returns { submission_id, resume_token, started_at }.
    try {
      const response = await apiClient.post(
        `/api/seq/surveys/${encodeURIComponent(surveySlug)}/submissions`,
        payload
      );
      return response.data;
    } catch (error) {
      console.error('Error creating submission:', error);
      throw error;
    }
  },

  async fetchSubmissionState(token) {
    // GET /api/seq/submissions/:token
    // Resume: { submission_id, status, revision, payload, answers, flow }
    // where answers and flow are keyed by item_key. Carries the
    // submission's OWN revision payload, which may differ from the
    // survey's current one if the questionnaire was edited while the
    // participant was away. 404 means the token is stale.
    try {
      const response = await apiClient.get(
        `/api/seq/submissions/${encodeURIComponent(token)}`
      );
      return response.data;
    } catch (error) {
      console.error('Error fetching submission state:', error);
      throw error;
    }
  },

  async saveAnswer(token, itemKey, payload) {
    // PUT /api/seq/submissions/:token/answers/:item_key
    // Idempotent upsert. Sends only the card's state; the backend derives
    // question_code, question_type and the extracted values from the
    // revision, so a client cannot mislabel research data.
    try {
      const response = await apiClient.put(
        `/api/seq/submissions/${encodeURIComponent(token)}/answers/${encodeURIComponent(itemKey)}`,
        { payload }
      );
      return response.data;
    } catch (error) {
      console.error('Error saving answer:', error);
      throw error;
    }
  },

  async saveFlowData(token, itemKey, payload) {
    // PUT /api/seq/submissions/:token/flow/:item_key
    // Same contract, for flow cards. Never feeds scoring.
    try {
      const response = await apiClient.put(
        `/api/seq/submissions/${encodeURIComponent(token)}/flow/${encodeURIComponent(itemKey)}`,
        { payload }
      );
      return response.data;
    } catch (error) {
      console.error('Error saving flow data:', error);
      throw error;
    }
  },

  async completeSubmission(token) {
    // POST /api/seq/submissions/:token/complete
    try {
      const response = await apiClient.post(
        `/api/seq/submissions/${encodeURIComponent(token)}/complete`
      );
      return response.data;
    } catch (error) {
      console.error('Error completing submission:', error);
      throw error;
    }
  },

  async openSurvey(surveySlug) {
    // POST /api/seq/surveys/:slug/open  (admin)
    // Binds the survey to its questionnaire's head revision and sets it
    // active, minting revision 1 first if the questionnaire was never
    // frozen. Returns { survey, revision, revision_minted }.
    try {
      const response = await apiClient.post(
        `/api/seq/surveys/${encodeURIComponent(surveySlug)}/open`
      );
      return response.data;
    } catch (error) {
      console.error('Error opening survey:', error);
      throw error;
    }
  },

  async fetchSurveyResults(surveySlug) {
    // GET /api/seq/surveys/:slug/results  (admin)
    // Aggregate figures for the results dashboard: participation, drop-off,
    // score distributions. Computed from the same model as the Excel
    // export, so the page and the file cannot disagree. Aggregate only; no
    // per-participant row is returned.
    try {
      const response = await apiClient.get(
        `/api/seq/surveys/${encodeURIComponent(surveySlug)}/results`
      );
      return response.data;
    } catch (error) {
      console.error('Error fetching survey results:', error);
      throw error;
    }
  },

  async downloadSurveyExport(surveySlug, locale = 'en') {
    // GET /api/seq/surveys/:slug/export  (admin)
    // Returns an .xlsx workbook: Responses, Codebook, Revisions, README.
    // Fetched as a blob rather than linked directly so the request carries
    // the auth header, and so a failure surfaces as an error rather than a
    // browser tab full of binary.
    try {
      const response = await apiClient.get(
        `/api/seq/surveys/${encodeURIComponent(surveySlug)}/export`,
        { params: { locale }, responseType: 'blob' }
      );
      return {
        blob: response.data,
        filename: parseFilename(response.headers['content-disposition'])
          || `seq_${surveySlug}.xlsx`,
      };
    } catch (error) {
      console.error('Error downloading survey export:', error);
      throw error;
    }
  },

  async fetchSurveyStats(surveySlug) {
    // GET /api/seq/surveys/:slug/stats  (admin)
    // Submission counts by status plus total answers.
    try {
      const response = await apiClient.get(
        `/api/seq/surveys/${encodeURIComponent(surveySlug)}/stats`
      );
      return response.data;
    } catch (error) {
      console.error('Error fetching survey stats:', error);
      throw error;
    }
  },

  async fetchQuestionnaireRevisions(slug) {
    // GET /api/seq/questionnaires/:slug/revisions
    // Immutable snapshots of the questionnaire, newest first, WITHOUT
    // payloads: [{ id, revision, change_kind, change_note, payload_hash,
    // created_at, created_by }]. See docs/CONTRACT_DATA_CAPTURE.md.
    try {
      const response = await apiClient.get(
        `/api/seq/questionnaires/${encodeURIComponent(slug)}/revisions`
      );
      return Array.isArray(response.data) ? response.data : [];
    } catch (error) {
      console.error('Error fetching questionnaire revisions:', error);
      throw error;
    }
  },

  async fetchQuestionnaireRevision(slug, revision) {
    // GET /api/seq/questionnaires/:slug/revisions/:n
    // One revision including its full frozen payload — the questionnaire
    // exactly as participants on that revision saw it.
    try {
      const response = await apiClient.get(
        `/api/seq/questionnaires/${encodeURIComponent(slug)}/revisions/${revision}`
      );
      return response.data;
    } catch (error) {
      console.error('Error fetching questionnaire revision:', error);
      throw error;
    }
  },

  async fetchQuestionnaireEditImpact(slug) {
    // GET /api/seq/questionnaires/:slug/edit-impact
    // Read-only. { has_revisions, head_revision, differs_from_head,
    // item_count, surveys: [{ slug, name, status, submission_count }],
    // total_submissions }. Drives the edit-warning dialog; note that
    // differs_from_head false means a mint would be a refused no-op.
    try {
      const response = await apiClient.get(
        `/api/seq/questionnaires/${encodeURIComponent(slug)}/edit-impact`
      );
      return response.data;
    } catch (error) {
      console.error('Error fetching questionnaire edit impact:', error);
      throw error;
    }
  },

  async mintQuestionnaireRevision(slug, payload) {
    // POST /api/seq/questionnaires/:slug/revisions
    // Freezes the questionnaire's current state as the next revision.
    // payload: { change_kind: 'cosmetic'|'substantive', change_note }.
    // Both are omitted for the very first ('initial') revision and
    // required for every one after it.
    //
    // 201 => { created: true, revision }. 200 => { created: false,
    // reason: 'unchanged', revision } when nothing actually changed —
    // that is a success, not an error, so callers must check `created`
    // rather than assuming a 2xx minted something.
    try {
      const response = await apiClient.post(
        `/api/seq/questionnaires/${encodeURIComponent(slug)}/revisions`,
        payload || {}
      );
      return response.data;
    } catch (error) {
      console.error('Error minting questionnaire revision:', error);
      throw error;
    }
  },

  async fetchFlowCards() {
    // GET /api/seq/flow-cards
    // Returns the global flow-card catalogue: [{ id, code, flow_type,
    // name (i18n dict), status }]. Drives the editor's Flow tab. Not
    // org-scoped; the add path (addItemToQuestionnaire) validates the id.
    try {
      const response = await apiClient.get('/api/seq/flow-cards');
      return Array.isArray(response.data) ? response.data : [];
    } catch (error) {
      console.error('Error fetching flow cards:', error);
      throw error;
    }
  },

  async fetchSectionsWithCounts({ includeArchived = false } = {}) {
    // GET /api/seq/sections?include_counts=true[&include_archived=true]
    // Returns the global section list scoped to the caller's org, each
    // row carrying { question_count, questionnaire_count }. Drives the
    // SectionPickerPopup.
    try {
      const params = { include_counts: true };
      if (includeArchived) params.include_archived = true;
      const response = await apiClient.get('/api/seq/sections', { params });
      return Array.isArray(response.data) ? response.data : [];
    } catch (error) {
      console.error('Error fetching sections with counts:', error);
      throw error;
    }
  },

  async createGlobalSection(payload) {
    // POST /api/seq/sections — body { slug, name }
    // Returns the created row. 409 on slug collision.
    const response = await apiClient.post('/api/seq/sections', payload);
    return response.data;
  },

  async patchGlobalSection(id, payload) {
    // PATCH /api/seq/sections/:id/global — body { slug?, name? }
    // The /global suffix exists during the transition so this endpoint
    // doesn't collide with the legacy per-questionnaire-scoped
    // PATCH /sections/:id. Once the legacy is removed, the suffix goes
    // away.
    const response = await apiClient.patch(`/api/seq/sections/${id}/global`, payload);
    return response.data;
  },

  async archiveGlobalSection(id) {
    // POST /api/seq/sections/:id/archive — returns the updated row.
    const response = await apiClient.post(`/api/seq/sections/${id}/archive`);
    return response.data;
  },

  async restoreGlobalSection(id) {
    // POST /api/seq/sections/:id/restore — returns the updated row.
    const response = await apiClient.post(`/api/seq/sections/${id}/restore`);
    return response.data;
  },

  async deleteGlobalSection(id) {
    // DELETE /api/seq/sections/:id/global — 204 on success.
    // 409 if the section still has references; the popup gates the
    // 🗑 icon client-side but the backend is the source of truth.
    const response = await apiClient.delete(`/api/seq/sections/${id}/global`);
    return response.data;
  },

  async fetchContentCards() {
    // GET /cards/list — existing public endpoint that returns every
    // content_cards row, ordered by sort_order ASC. No /api prefix
    // (the legacy cards router was mounted at root for v1 public access).
    // Wraps the response: the endpoint returns { cards: [...] }; the
    // store consumes just the array.
    try {
      const response = await apiClient.get('/cards/list');
      return Array.isArray(response.data?.cards) ? response.data.cards : [];
    } catch (error) {
      console.error('Error fetching content cards:', error);
      throw error;
    }
  },

  async addItemToQuestionnaire(slug, payload) {
    // POST /api/seq/questionnaires/:slug/items
    // Body: { section_id, item_type, item_id }
    // Returns the new junction row in the resolved-item shape so the
    // caller can append directly to local state without a refetch.
    // 422 on validation failure (section mismatch, source row missing,
    // CHECK violation). 404 if the questionnaire is not in this org.
    const response = await apiClient.post(
      `/api/seq/questionnaires/${encodeURIComponent(slug)}/items`,
      payload,
    );
    return response.data;
  },

  async reorderItemsInSection(slug, sectionId, orderedIds) {
    // PATCH /api/seq/questionnaires/:slug/items/order
    // Body: { section_id, ordered_ids }
    // The ordered_ids must be a permutation of the current items in
    // that section; mismatches return 422 with a {missing, extra}
    // detail object (mirrors the legacy seq_cards reorder shape).
    // Returns 204 No Content.
    const response = await apiClient.patch(
      `/api/seq/questionnaires/${encodeURIComponent(slug)}/items/order`,
      { section_id: sectionId, ordered_ids: orderedIds },
    );
    return response.data;
  },

  async removeItemFromQuestionnaire(slug, itemId) {
    // DELETE /api/seq/questionnaires/:slug/items/:id
    // Removes one junction row; the source row in seq_questions /
    // content_cards / seq_flow_cards is untouched. 204 on success,
    // 404 if the questionnaire or item is not visible to this user.
    const response = await apiClient.delete(
      `/api/seq/questionnaires/${encodeURIComponent(slug)}/items/${itemId}`,
    );
    return response.data;
  },

  async createSectionForQuestionnaire(slug, payload) {
    // POST /api/seq/questionnaires/:slug/sections
    // Body: { slug, name: {de?, en?, fr?, it?}, position? }
    // Position is optional — backend assigns MAX(position)+1 if omitted.
    // Returns the created row {id, slug, position, name, created_at,
    //                          updated_at}. Auth required.
    // 404 if the questionnaire is not in caller's org.
    // 409 if (questionnaire_id, section.slug) already exists.
    // 422 on slug regex / shape failures (Pydantic side).
    try {
      const response = await apiClient.post(
        `/api/seq/questionnaires/${encodeURIComponent(slug)}/sections`,
        payload,
      );
      return response.data;
    } catch (error) {
      console.error('Error creating SEQ section:', error);
      throw error;
    }
  },

  async updateSection(id, payload) {
    // PATCH /api/seq/sections/:id
    // Body (all fields optional): { name?: {de?, en?, fr?, it?} }
    // Returns the full updated row {id, slug, position, name,
    //                                created_at, updated_at}.
    // 404 if section is missing or in another org.
    // 422 on schema CHECK violations.
    try {
      const response = await apiClient.patch(`/api/seq/sections/${id}`, payload);
      return response.data;
    } catch (error) {
      console.error('Error updating SEQ section:', error);
      throw error;
    }
  },

  async deleteSection(id) {
    // DELETE /api/seq/sections/:id
    // 204 No Content on success.
    // 404 if section is missing or in another org.
    // 409 if section still has cards (UX rule: must be emptied first;
    // stricter than the schema's ON DELETE SET NULL on seq_cards.section_id).
    // Error response is rethrown so the caller can read
    // error.response.status and error.response.data.detail.
    try {
      await apiClient.delete(`/api/seq/sections/${id}`);
    } catch (error) {
      console.error('Error deleting SEQ section:', error);
      throw error;
    }
  },

  async createCardInSection(sectionId, payload) {
    // POST /api/seq/sections/:id/cards
    // Body: { card_type, code, content?, position?, is_required? }
    //   - card_type: 'question' | 'section_complete' | 'info'
    //   - code: ^[A-Z][A-Z0-9_]{0,31}$
    //   - content: JSONB object; for question cards include input_style
    //   - position: optional; backend appends within the section
    //   - is_required: optional; backend defaults True for question
    // Returns the created row {id, section_id, card_type, code,
    //   position, content, is_required, created_at, updated_at}.
    // 404 if section is missing or in another org.
    // 409 if (questionnaire_id, code) is already taken.
    // 422 on code regex / shape failures.
    try {
      const response = await apiClient.post(
        `/api/seq/sections/${sectionId}/cards`,
        payload,
      );
      return response.data;
    } catch (error) {
      console.error('Error creating SEQ card:', error);
      throw error;
    }
  },

  async deleteCard(id) {
    // DELETE /api/seq/cards/:id
    // 204 No Content on success.
    // 404 if card is missing or in another org.
    // No 409 path: cards have no children to orphan.
    try {
      await apiClient.delete(`/api/seq/cards/${id}`);
    } catch (error) {
      console.error('Error deleting SEQ card:', error);
      throw error;
    }
  },

  async reorderCardsInSection(sectionId, cardIds) {
    // PATCH /api/seq/sections/:id/cards/order
    // Body: { card_ids: [12, 14, 11, ...] } — full ordered list of
    // cards currently in this section. Backend rejects partial lists,
    // duplicates, and ids that don't belong to the section.
    // 204 No Content on success.
    // 404 if section is missing or in another org.
    // 422 if card_ids isn't an exact permutation of the section's cards;
    //     response detail is { message, missing: [], extra: [] } so the
    //     caller can render a specific error or trigger a refresh.
    try {
      await apiClient.patch(
        `/api/seq/sections/${sectionId}/cards/order`,
        { card_ids: cardIds },
      );
    } catch (error) {
      console.error('Error reordering SEQ cards:', error);
      throw error;
    }
  },

  async reorderSectionsInQuestionnaire(slug, sectionIds) {
    // PATCH /api/seq/questionnaires/:slug/sections/order
    // Body: { section_ids: [3, 1, 2, ...] } — full ordered list of
    // sections currently in this questionnaire. Backend rejects partial
    // lists, duplicates, and ids that don't belong to the questionnaire.
    // 204 No Content on success.
    // 404 if questionnaire is missing or in another org.
    // 422 if section_ids isn't an exact permutation; response detail
    //     is { message, missing: [], extra: [] }.
    try {
      await apiClient.patch(
        `/api/seq/questionnaires/${encodeURIComponent(slug)}/sections/order`,
        { section_ids: sectionIds },
      );
    } catch (error) {
      console.error('Error reordering SEQ sections:', error);
      throw error;
    }
  },

  // Organization management methods (protected endpoints)
  async fetchOrganizations() {
    try {
      const response = await apiClient.get('/organizations');
      return response.data;
    } catch (error) {
      console.error('Error fetching organizations:', error);
      throw error;
    }
  },

  async createOrganization(orgData) {
    try {
      const response = await apiClient.post('/organizations', orgData);
      return response.data;
    } catch (error) {
      console.error('Error creating organization:', error);
      throw error;
    }
  },

  async updateOrganization(orgId, orgData) {
    try {
      const response = await apiClient.put(`/organizations/${orgId}`, orgData);
      return response.data;
    } catch (error) {
      console.error('Error updating organization:', error);
      throw error;
    }
  },

  async deleteOrganization(orgId) {
    try {
      const response = await apiClient.delete(`/organizations/${orgId}`);
      return response.data;
    } catch (error) {
      console.error('Error deleting organization:', error);
      throw error;
    }
  },

  // Classes management methods (protected endpoints)
  async createClass(classData) {
    try {
      const response = await apiClient.post('/classes', classData);
      return response.data;
    } catch (error) {
      console.error('Error creating class:', error);
      throw error;
    }
  },

  async updateClass(classId, classData) {
    try {
      const response = await apiClient.put(`/classes/${classId}`, classData);
      return response.data;
    } catch (error) {
      console.error('Error updating class:', error);
      throw error;
    }
  },

  async deleteClass(classId) {
    try {
      const response = await apiClient.delete(`/classes/${classId}`);
      return response.data;
    } catch (error) {
      console.error('Error deleting class:', error);
      throw error;
    }
  },

  // Student management methods (protected endpoints)
  async createStudent(studentData) {
    try {
      const response = await apiClient.post('/students', studentData);
      return response.data;
    } catch (error) {
      console.error('Error creating student:', error);
      throw error;
    }
  },

  async updateStudent(studentId, studentData) {
    try {
      const response = await apiClient.put(`/students/${studentId}`, studentData);
      return response.data;
    } catch (error) {
      console.error('Error updating student:', error);
      throw error;
    }
  },

  async deleteStudent(studentId) {
    try {
      const response = await apiClient.delete(`/students/${studentId}`);
      return response.data;
    } catch (error) {
      console.error('Error deleting student:', error);
      throw error;
    }
  },

  async uploadStudentsFile(classId, file) {
    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await apiClient.post(`/classes/${classId}/upload-students`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      return response.data;
    } catch (error) {
      console.error('Error uploading students file:', error);
      throw error;
    }
  },

  // Additional methods for MyAssessmentsPage

  async fetchStudentsProgress(assessmentId, studentIds) {
    try {
      const response = await apiClient.post(`/assessments/${assessmentId}/students-progress`, {
        student_ids: studentIds
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching students progress:', error);
      throw error;
    }
  },

  async checkStudentResponses(assessmentId, studentId) {
    try {
      const response = await apiClient.post(`/assessments/${assessmentId}/check-student-responses`, {
        student_id: studentId
      });
      return response.data;
    } catch (error) {
      console.error('Error checking student responses:', error);
      throw error;
    }
  },

  async clearStudentResponses(assessmentId, studentId) {
    try {
      const response = await apiClient.post(`/assessments/${assessmentId}/clear-student-responses`, {
        student_id: studentId
      });
      return response.data;
    } catch (error) {
      console.error('Error clearing student responses:', error);
      throw error;
    }
  },

  async fetchAllAssessments() {
    try {
      const response = await apiClient.get('/assessments');
      return response.data;
    } catch (error) {
      console.error('Error fetching all assessments:', error);
      throw error;
    }
  },

  async fetchNetworkSurveyTopics() {
    try {
      const response = await apiClient.get('/api/networksurveys');
      return response.data;
    } catch (error) {
      console.error('Error fetching network survey topics:', error);
      throw error;
    }
  },

  async toggleAssessmentStatus(assessmentId, payload) {
    try {
      const response = await apiClient.put(`/assessments/${assessmentId}`, payload, {
        headers: { 'Content-Type': 'application/json' },
      });
      return response.data;
    } catch (error) {
      console.error('Error toggling assessment status:', error);
      throw error;
    }
  },

  async saveAssessmentData(assessmentId, payload) {
    try {
      let response;
      if (assessmentId) {
        response = await apiClient.put(`/assessments/${assessmentId}`, payload, {
          headers: { 'Content-Type': 'application/json' },
        });
      } else {
        response = await apiClient.post('/assessments', payload, {
          headers: { 'Content-Type': 'application/json' },
        });
      }
      return response.data;
    } catch (error) {
      console.error('Error saving assessment:', error);
      throw error;
    }
  },

  // Password reset methods
  async getUserByUsername(username) {
    try {
      const response = await apiClient.get(`/users/by-username/${username}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching user by username:', error);
      throw error;
    }
  },

  async verifyUserPassword(username, password) {
    try {
      // Use the token endpoint to verify credentials
      const formData = new URLSearchParams();
      formData.append('username', username);
      formData.append('password', password);

      const response = await axios.post(`${API_URL}/token`, formData.toString(), {
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded'
        }
      });

      return {
        success: true,
        token: response.data.access_token,
        userId: response.data.userId
      };
    } catch (error) {
      console.error('Error verifying user password:', error);
      return {
        success: false,
        error: 'Invalid username or password'
      };
    }
  },

async resetUserPassword(username, currentPassword, newPassword) {
  try {
    // Use the new public password reset endpoint
    const response = await axios.post(`${API_URL}/users/reset-password`, {
      username: username,
      current_password: currentPassword,
      new_password: newPassword
    }, {
      headers: {
        'Content-Type': 'application/json'
      }
    });

    return { 
      success: true, 
      data: response.data 
    };
  } catch (error) {
    console.error('Error resetting password:', error);
    
    let errorMessage = 'An error occurred while resetting password';
    if (error.response?.status === 401) {
      errorMessage = 'Current password is incorrect';
    } else if (error.response?.status === 404) {
      errorMessage = 'User not found';
    }
    
    throw new Error(errorMessage);
  }
},

  // Profile management methods (protected endpoints)
  async fetchUserProfile(userId) {
    try {
      const response = await apiClient.get(`/profile/${userId}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching user profile:', error);
      throw error;
    }
  },

  async createUserProfile(profileData) {
    try {
      const response = await apiClient.post('/profile', profileData);
      return response.data;
    } catch (error) {
      console.error('Error creating user profile:', error);
      throw error;
    }
  },

  async updateUserProfile(userId, profileData) {
    try {
      const response = await apiClient.put(`/profile/${userId}`, profileData);
      return response.data;
    } catch (error) {
      console.error('Error updating user profile:', error);
      throw error;
    }
  },

  async deleteUserProfile(userId) {
    try {
      const response = await apiClient.delete(`/profile/${userId}`);
      return response.data;
    } catch (error) {
      console.error('Error deleting user profile:', error);
      throw error;
    }
  },

  async updateUserStatus(userId, status) {
    try {
      const response = await apiClient.put(`/users/${userId}/status`, { status });
      return response.data;
    } catch (error) {
      console.error('Error updating user status:', error);
      throw error;
    }
  },

};
